import {
  LoanProgram,
  RateDetails,
  ProductGroup,
} from '@frontend/pricing-search';
import { DeepPartial, parseNumber } from '@frontend/utils';
export interface Target {
  isTargetPrice: boolean;
  value?: number;
}
export interface SortableProperties {
  delta: number;
  tPrimary: number;
  tSecondary: number;
  discountPoints?: number;
  target?: SortableRateDetails;
  price?: number;
  rate?: number;
  indexRow?: any;
  productGroupID?: number;
}
type SortableLoanProgram = DeepPartial<LoanProgram> & SortableProperties;
type SortableRateDetails = DeepPartial<RateDetails> & SortableProperties;
type SortableProductGroup = DeepPartial<ProductGroup> & SortableProperties;

export function makeSortDelta(isZeroBasedPricing, isTargetPrice) {
  const THRESH = isZeroBasedPricing ? 50 : 100;
  return function sortDelta(a, b) {
    // first check error state
    if (a.deleted !== 0 && b.deleted !== 0) return a.deleted - b.deleted;
    if (a.deleted !== 0) return 1;
    if (b.deleted !== 0) return -1;

    // Target Rate scenarios (isTargetPrice === false)
    if (!isTargetPrice) {
      // Zero-based: sort rate asc, if rates match, sort price asc
      if (isZeroBasedPricing) {
        if (a.tPrimary === b.tPrimary) return a.tSecondary - b.tSecondary;
        return a.tPrimary - b.tPrimary;
      }
      // Hundred-based: sort rate desc, if rates match, sort price desc
      if (a.tPrimary === b.tPrimary) return b.tSecondary - a.tSecondary;
      return a.tPrimary - b.tPrimary;
    }

    // Both programs have pricing at or above target
    const bothAtOrAbove = a.delta < THRESH && b.delta < THRESH;

    // Target Price scenarios (isTargetPrice === true)
    if (bothAtOrAbove) {
      // Hundred-based: if rates match, sort price desc
      if (!isZeroBasedPricing && a.tSecondary === b.tSecondary) {
        return b.tPrimary - a.tPrimary;
      }
      // Otherwise sort secondary (rate) asc, then primary (price) asc
      if (a.tSecondary === b.tSecondary) return a.tPrimary - b.tPrimary;
      return a.tSecondary - b.tSecondary;
    }
    // only program a has target pricing
    if (a.delta < THRESH) return -1;
    // only program b has target pricing
    if (b.delta < THRESH) return 1;
    // both programs are below target
    if (a.tPrimary === b.tPrimary) return a.tSecondary - b.tSecondary;
    return a.tPrimary - b.tPrimary;
  };
}
export function calcDeltas(
  aProgs: SortableLoanProgram[],
  target: Target,
  isZeroBasedPricing: boolean,
): SortableLoanProgram[] {
  // get the best delta for each rate
  const hRate: Record<string, SortableRateDetails> = {};
  const v = Number(target.value ?? 0);
  const t = target.isTargetPrice
    ? isZeroBasedPricing && v === 100
      ? 0
      : 100 - v
    : v;
  let od: number;
  let r: SortableRateDetails;
  let prim: number;
  let sec: number;
  aProgs.forEach((prog) => {
    const aRates = prog.rateDetails;
    if (aRates) {
      const l = aRates.length;
      // Check if all primary values (price for target pricing, rate for target rate) are below target
      const allPrimaryValuesBelowTarget = aRates.every((rate) => {
        if (!rate) return false;
        const primaryValue = target.isTargetPrice
          ? typeof rate.price === 'number'
            ? rate.price
            : null
          : typeof rate.rate === 'number'
          ? rate.rate
          : null;
        if (primaryValue === null) return false;
        return !(isZeroBasedPricing
          ? t - primaryValue >= 0
          : primaryValue >= t);
      });
      // Check if all primary values are above target for zero based pricing
      let allPrimaryValuesAboveTarget = false;
      if (isZeroBasedPricing && target.isTargetPrice) {
        allPrimaryValuesAboveTarget = aRates.every((rate) => {
          if (!rate) return false;
          let primaryValue: number | null = null;
          if (typeof rate.price === 'number') {
            primaryValue = rate.price;
          }
          if (primaryValue === null) return false;
          return t - primaryValue < 0;
        });
      }

      for (let i = 0; i < l; i += 1) {
        r = aRates[i] as SortableRateDetails;
        od = hRate[prog.programID as number]?.delta ?? 999;
        prim = (target.isTargetPrice ? r.price : r.rate) as number;
        sec = (target.isTargetPrice ? r.rate : r.price) as number;
        if (isZeroBasedPricing ? t - prim >= 0 : prim >= t) {
          r.delta = sec;
        } else if (!isZeroBasedPricing && allPrimaryValuesBelowTarget) {
          // When zero-based pricing is FALSE and ALL primary values are below target: use negative primary value to prioritize highest value
          r.delta = -prim;
        } else if (allPrimaryValuesAboveTarget) {
          // When zero-based pricing is TRUE and ALL primary values are above target: pick closest price above target as eligible
          r.delta = prim;
        } else {
          r.delta = prim + 100;
        }
        if (r.delta < od) {
          // hDelta.set(r.ProgramID, r.delta);
          // hDelta.set(r.ProgramID, r.delta);
          hRate[prog.programID as number] = r;
        }
      }
    }
  });

  // update the programs w/ deltas
  const l = aProgs.length;
  let p: SortableLoanProgram;
  for (let i = 0; i < l; i += 1) {
    p = aProgs[i];
    const d = hRate[p.programID as number];
    if (d) {
      p.delta = d.delta;
      p.target = d;
      d.discountPoints = parseNumber((d.price as number) * -1, null);
      Object.assign(p, d);
      if (target.isTargetPrice) {
        p.tPrimary = d.price as number;
        p.tSecondary = d.rate as number;
      } else {
        p.tPrimary = d.rate as number;
        p.tSecondary = d.price as number;
      }
    } else p.delta = 999;
  }

  // sort programs array
  return aProgs.sort(makeSortDelta(isZeroBasedPricing, target.isTargetPrice));
}

export function calcDeltasBestEx(
  aGroups: SortableProductGroup[],
  aRatesBX: any,
  target: Target,
  isZeroBasedPricing: boolean,
): SortableProductGroup[] {
  // get the best delta for each rate
  const hRate: Record<string, SortableRateDetails> = {};
  const l = aRatesBX.length;
  const v = Number(target.value ?? 0);
  const t = target.isTargetPrice
    ? isZeroBasedPricing && v === 100
      ? 0
      : 100 - v
    : v;
  let od: number;
  let r: SortableRateDetails;
  let prim: number;
  let sec: number;
  for (let i = 0; i < l; i += 1) {
    r = aRatesBX[i];
    od = hRate[r.productGroupID as number]?.delta ?? 999;
    prim = (target.isTargetPrice ? r.price : r.rate) as number;
    sec = (target.isTargetPrice ? r.rate : r.price) as number;
    if (isZeroBasedPricing ? t - prim >= 0 : prim >= t) r.delta = sec;
    else r.delta = prim + 100;
    if (r.delta < od) {
      hRate[r.productGroupID as number] = r;
    }
  }
  // update the product groups w/ deltas
  aGroups.forEach((g) => {
    const group = g; // Reference to g
    r = hRate[g.productGroupID as number];
    if (r) {
      group.delta = r.delta;
      group.target = r;
      if (target.isTargetPrice) {
        group.tPrimary = r.price as number;
        group.tSecondary = r.rate as number;
      } else {
        group.tPrimary = r.rate as number;
        group.tSecondary = r.price as number;
      }
    } else group.delta = 999;
    // index the rates for expanding/collapsing
    if (group.target !== undefined && Array.isArray(group.rateDetails)) {
      for (let i = 0; i < group.rateDetails.length; i += 1) {
        const row = group.rateDetails[i] as SortableRateDetails;
        if (row) row.indexRow = i;
      }
    }
    // if (group.target !== undefined) {
    //   for (let i = 0; i < group.rateDetails.length; i += 1) {
    //     if (group.rateDetails[i].rate === group.target.Rate) {
    //       group.targetIndex = i;
    //       break;
    //     }
    //   }
    //   for (let i = 0; i < group.rateDetails.length; i += 1) {
    //     if (i === group.targetIndex) group.rateDetails[i].expandLevel = 1;
    //     else if (Math.abs(group.targetIndex - i) <= 3)
    //       group.rateDetails[i].expandLevel = 2;
    //     else group.rateDetails[i].expandLevel = 3;
    //   }
    // }
  });
  return aGroups;
}
